import { ApiResponseError } from '../ApiResponseError';
import { WebIdProfileCheckResponse } from './model/WebIdProfileCheckResponse';
import { WebIdStartAutoIdentRequest } from './model/WebIdStartAutoIdentRequest';
import { WebIdStartAutoIdentResponse } from './model/WebIdStartAutoIdentResponse';
import { WebIdVerificationAttempt } from './model/WebIdVerificationAttempt';
import { UserVerifiedAt } from './model/UserVerifiedAt';

const webIdServiceBaseUrl = (): string => process.env.WEBID_SERVICE_BASE_URL || 'https://random-webid-backend.example';

const webIdApiPath = (path: string, userId?: string): string => `${userId ? '/light-user' : ''}${path}`;

const webIdHeaders = (userId?: string): Record<string, string> => ({
  Accept: 'application/json; charset=utf-8',
  'Content-Type': 'application/json; charset=UTF-8',
  ...(userId ? { Authorization: `Bearer ${userId}` } : {}),
});

const postWebIdJson = async <ResponseBody>(
  path: string,
  body?: Record<string, string | boolean>,
  userId?: string,
): Promise<ResponseBody> => {
  const response = await fetch(`${webIdServiceBaseUrl()}${webIdApiPath(path, userId)}`, {
    method: 'POST',
    headers: webIdHeaders(userId),
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new ApiResponseError(response.status, response);
  }

  if (response.status === 204) {
    return {} as ResponseBody;
  }

  const responseText = await response.text();
  if (!responseText) {
    return {} as ResponseBody;
  }

  return JSON.parse(responseText) as ResponseBody;
};

export const startWebIdVerification = async (
  payload: WebIdVerificationAttempt,
  userId?: string,
): Promise<WebIdProfileCheckResponse> => {
  return postWebIdJson<WebIdProfileCheckResponse>('/api/trueid/check-user-profile', payload, userId);
};

export const startWebIdAutoIdent = async (
  payload: WebIdStartAutoIdentRequest,
  userId?: string,
): Promise<WebIdStartAutoIdentResponse> => {
  return postWebIdJson<WebIdStartAutoIdentResponse>('/api/trueid/start-autoident', payload, userId);
};

export const verifyWebIdTan = async (code: string, sessionId: string, userId?: string): Promise<void> => {
  await postWebIdJson<void>(`/api/trueid/sessions/${sessionId}/tan/verify`, { code }, userId);
};

export const sendWebIdTan = async (sessionId: string, userId?: string): Promise<void> => {
  await postWebIdJson<void>(`/api/trueid/sessions/${sessionId}/tan/send`, undefined, userId);
};

export const getWebIdSessionVerifiedAt = async (sessionId: string, userId?: string): Promise<UserVerifiedAt> => {
  const response = await fetch(
    `${webIdServiceBaseUrl()}${webIdApiPath(`/api/trueid/session/${sessionId}/verified-at`, userId)}`,
    {
      method: 'GET',
      headers: webIdHeaders(userId),
      credentials: 'include',
    },
  );

  if (!response.ok) {
    throw new ApiResponseError(response.status, response);
  }

  return (await response.json()) as UserVerifiedAt;
};
